import {DataStatus, openDialog, showNotification} from 'platform/components';
import {Heading, Text, VStack} from 'platform/foundation';

import {isEmpty} from 'ramda';

import {
  UserMailboxResponseBody,
  useDeleteUserMailboxMutation,
  useListUserMailboxesQuery,
  useReauthenticateUserMailboxMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {buildUrl, userMailboxOAuthFinish} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {CreateUserMailboxDialog} from './CreateUserMailboxDialog';
import {UserMailboxCard} from './UserMailboxCard';

export function ReceivingMailboxesSection() {
  const {data, isLoading, isError} = useListUserMailboxesQuery();

  const [deleteUserMailbox] = useDeleteUserMailboxMutation();
  const [reauthenticateUserMailbox] = useReauthenticateUserMailboxMutation();

  const mailboxes: UserMailboxResponseBody[] = data?.mailboxes ?? [];

  const handleCreate = () =>
    openDialog(<CreateUserMailboxDialog />, {
      title: i18n.t('page.myProfile.receivingMailbox.createDialog.title'),
    });

  const handleDelete = async (id: string) => {
    await deleteUserMailbox({mailboxId: id})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.myProfile.receivingMailbox.notifications.removed'));
      })
      .catch(handleApiError);
  };

  const handleReauthenticate = async (id: string) => {
    const redirectUri = buildUrl(userMailboxOAuthFinish, undefined, false);
    await reauthenticateUserMailbox({
      mailboxId: id,
      reauthenticateUserMailboxRequestBody: {redirectUri},
    })
      .unwrap()
      .then(({oauthUrl}) => {
        window.location.href = oauthUrl;
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('page.myProfile.receivingMailbox.sectionTitle')}</Heading>
        <Text size="small" color="secondary">
          {i18n.t('page.myProfile.receivingMailbox.sectionDescription')}
        </Text>
      </VStack>
      <DataStatus
        spacing={10}
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty(mailboxes)}
        action={{
          title: i18n.t('general.labels.createNew'),
          onClick: handleCreate,
          leftIcon: 'content/add_circle',
        }}
      >
        <VStack spacing={4}>
          {mailboxes.map((mailbox) => (
            <UserMailboxCard
              key={mailbox.id}
              mailbox={mailbox}
              onDelete={handleDelete}
              onReauthenticate={handleReauthenticate}
            />
          ))}
        </VStack>
      </DataStatus>
    </VStack>
  );
}
